import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Dialog, DialogContent, DialogActions,
    Box, Typography, Button, IconButton,
    TextField, Radio, RadioGroup, FormControlLabel,
    Checkbox, MenuItem, Divider, Chip, Autocomplete,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import { getCountries, useGeo } from '../../../../../../utils/geo';
import { eu_countries } from '../../../../../../utils/eu_countries';
import { LANGUAGES_SUPPORTED, DQC_COLORS } from '../../../../../../utils/constants';
import {
    DELIVERY_FORMATS, SUBJECTS, TECH_ENVIRONMENTS,
    TEACHING_METHODS, SENSITIVITIES, CONTENT_STYLES,
    TIERS, getTierLabel, TIER_TO_AGE_MAP, resolveTier,
    getCompetencyCap, normalizeCompetencyCodes, isCompetencyInSolutionScope, resolveCompetencyOptions,
    TIER_ACTIVITIES, getAvailableActivitiesForTier,
    getRecommendedActivitiesForTier, getRecommendedActivityLabels,
} from '../../../../../../utils/dq_program_constants';
import { getSolutionDqcRange } from '../../../../../../utils/productAccess';
import { useAuth } from '../../../../../../context/auth';
import {
    listV3Level7FrameService,
    listV3Level7DQCodeService,
    buildLearningProgramService,
    getInsiderProgramService,
    loadChatStateInsiderService,
    saveChatStateInsiderService,
    getLearningProgramEditCostService,
} from '../../../../../../services/learning_programs.service';
import { useColorMode } from '../../../../../../context/colorMode/colorMode.context';
import { canBuy, topUpLabel } from '../../../utils/credit_shortfall.utils';


const ORANGE = '#EE6628';

const fmtUnits = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

/**
 * What saving the edit costs, as the API priced it: free for the program's first
 * edit, the rebuild's credits (or DQ Tokens) after that, and — when the pool is short —
 * what is missing, with Top Up for the workspace owner. Says nothing about credits
 * on a plan that does not charge, or while the price is unknown.
 */
export const EditCostLine = ({ cost, onTopUpCredits }) => {
    if (!cost) return null;
    if (cost.free_edit) return <strong>This first edit of the program is free.</strong>;
    if (!cost.charged) return null;
    const price = cost.dqt ? `${fmtUnits(cost.dqt.required)} DQ Tokens` : `${fmtUnits(cost.required_credits)} credits`;
    if (cost.sufficient) return <>This edit uses <strong>{price}</strong>.</>;
    const payer = cost.shortfall?.payer;
    return (
        <>
            This edit needs <strong>{price}</strong>. {cost.message}
            {onTopUpCredits && canBuy(payer) && (
                <>
                    {' '}
                    <Box component="span" onClick={() => onTopUpCredits(cost.shortfall)} sx={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                        {topUpLabel(payer)}
                    </Box>
                </>
            )}
        </>
    );
};
const HEADER_BG = '#EE6628';
const DEFAULT_CONTENT_LANGUAGE = 'en-US';
/*
 * Translated versions are paused (team decision, Seongmin 24 Sep): the Content
 * Language field is hidden so it is neither collected nor changed here. The
 * program's saved ContentLanguages is left exactly as it was. Set to true to
 * bring the field back when translations return.
 */
const SHOW_CONTENT_LANGUAGES = false;

// TEACHING_METHODS has values 0-7; value 8 = "Others" (was commented out in constants)
const TEACHING_METHODS_WITH_OTHER = [
    ...TEACHING_METHODS,
    { value: 8, label: 'Others', description: '' },
];

/*
 * The session-duration choices are gone with their control — slide 11, "the
 * user does not select the session duration either". The engine generates every
 * session at 30 to 45 minutes and never reads a length the person picked, so
 * offering these four was asking a question whose answer was thrown away.
 */

const EXPOSURE_OPTIONS = [
    { label: 'No prior exposure', val: 1, desc: '' },
    { label: 'Some informal exposure', val: 2, desc: '(e.g., social-media, peer discussions)' },
    { label: 'Formal exposure', val: 3, desc: '(e.g., classroom learning)' },
    { label: 'Uncertain', val: 4, desc: '' },
];

const FOCUS_IMPORTANCE_LABELS = ['Not Important', 'Important', 'Very Important'];

const PRIMARY_FOCUS_FIELDS = [
    { key: 'FocusProblem',  label: 'Understanding the problems and issues related to the topic' },
    { key: 'FocusSolution', label: 'Exploring responses and solutions' },
    { key: 'FocusMindset',  label: 'Encouraging a positive mindset' },
];
const KS_FOCUS_FIELDS = [
    { key: 'FocusKS_F', label: 'Raising awareness and acquiring factual knowledge' },
    { key: 'FocusKS_C', label: 'Gaining conceptual understanding' },
    { key: 'FocusKS_P', label: 'Developing practical and procedural skills' },
    { key: 'FocusKS_M', label: 'Building metacognitive and higher-order thinking skills' },
];
const AB_FOCUS_FIELDS = [
    { key: 'FocusAB_A', label: 'Shaping positive attitudes' },
    { key: 'FocusAB_P', label: 'Strengthening values and beliefs' },
    { key: 'FocusAB_I', label: "Discovering learners' intentions and willingness to change" },
    { key: 'FocusAB_B', label: 'Encouraging self-reflection and behavioral habits' },
];

// ── Shared sub-components ──────────────────────────────────────────────────

const SectionHeader = ({ title, isDark }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, mt: 1 }}>
        <Box sx={{ width: 4, height: 22, borderRadius: 2, background: ORANGE, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--brand-text, #1a1a1a)' }}>{title}</Typography>
    </Box>
);

const FieldLabel = ({ children, optional }) => (
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', mb: 0.5 }}>
        {children}
        {optional && <Box component="span" sx={{ fontWeight: 400, color: 'var(--brand-text-muted, #888)', ml: 0.5 }}>(optional)</Box>}
    </Typography>
);

const SubSectionLabel = ({ children }) => (
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: '#2D7DD2', mb: 1.5, mt: 2 }}>
        {children}
    </Typography>
);

const FocusRatingRow = ({ label, value, onChange, isDark }) => (
    <Box sx={{ mb: 2.5, pb: 2, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#F0F0F0'}` }}>
        <Typography sx={{ fontSize: 14, color: 'var(--brand-text, #333)', mb: 1 }}>{label}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {FOCUS_IMPORTANCE_LABELS.map((lbl, idx) => {
                const val = idx + 1;
                const selected = value === val;
                return (
                    <Button key={val} onClick={() => onChange(val)} sx={{
                        width: 160, fontSize: 13, textTransform: 'none',
                        fontWeight: selected ? 700 : 400, borderRadius: '8px',
                        border: `1.5px solid ${selected ? ORANGE : (isDark ? 'rgba(255,255,255,0.15)' : '#D0D0D0')}`,
                        background: selected ? ORANGE : (isDark ? 'transparent' : '#fff'),
                        color: selected ? '#fff' : 'var(--brand-text-muted, #555)',
                        '&:hover': { background: selected ? '#d44d16' : (isDark ? 'rgba(255,255,255,0.08)' : '#f5f5f5') },
                    }}>{lbl}</Button>
                );
            })}
        </Box>
    </Box>
);

// A checkbox-style selectable row used for formats, tech, teaching methods, sensitivities
const SelectableRow = ({ checked, disabled, onClick, label, sublabel, useRadio = false, isDark }) => (
    <Box onClick={() => !disabled && onClick()} sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        p: 1.5, borderRadius: '8px',
        border: `1.5px solid ${checked ? ORANGE : (isDark ? 'rgba(255,255,255,0.12)' : '#E0E0E0')}`,
        background: checked ? (isDark ? 'rgba(238,102,40,0.15)' : '#FFF5F0') : disabled ? (isDark ? 'rgba(255,255,255,0.04)' : '#F8F8F8') : (isDark ? 'transparent' : '#FAFAFA'),
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 0.15s',
        '&:hover': !disabled ? { borderColor: ORANGE, background: isDark ? 'rgba(238,102,40,0.15)' : '#FFF5F0' } : {},
    }}>
        {useRadio ? (
            <Radio checked={checked} onChange={onClick} onClick={e => e.stopPropagation()}
                sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE }, p: 0 }} size="small" />
        ) : (
            <Checkbox checked={checked} onChange={onClick} onClick={e => e.stopPropagation()}
                disabled={disabled}
                sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE }, p: 0, mt: 0.25 }} size="small" />
        )}
        <Box>
            <Typography sx={{ fontSize: 14, fontWeight: checked ? 600 : 400 }}>{label}</Typography>
            {sublabel && <Typography sx={{ fontSize: 12, color: 'var(--brand-text-muted, #777)', mt: 0.25 }}>{sublabel}</Typography>}
        </Box>
    </Box>
);

// ── Normalise BestDelivery ─────────────────────────────────────────────────
// Initial state uses a 9-element bit-array [0,0,0,0,0,0,0,0,0].
// After the wizard runs it becomes a value-array like [0, 2, 5].
// Detect by: length === 9 AND all values 0 or 1 → convert to value-array.
const normaliseBestDelivery = (raw) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    if (raw.length === 9 && raw.every(v => v === 0 || v === 1)) {
        return raw.reduce((acc, v, i) => (v === 1 ? [...acc, i] : acc), []);
    }
    return raw.filter(v => typeof v === 'number');
};

// ── Main component ─────────────────────────────────────────────────────────

const NONE_FRAMEWORK = { value: 'none', framework_id: 'none', label: 'No Specific Framework', DQCO: [] };

const hasMeaningfulWizardData = (wz = {}) => {
    if (!wz || typeof wz !== 'object') return false;
    return Boolean(
        wz.Age ||
        wz.Theme ||
        wz.Title ||
        wz.SessionDuration ||
        wz.NumSessions ||
        (Array.isArray(wz.Competencies) && wz.Competencies.length > 0) ||
        (Array.isArray(wz.SelectedFrameList) && wz.SelectedFrameList.length > 0)
    );
};

const EditProgramInfoDialog = ({ open, onClose, wizard, programCode, onSave, onSaved, chatHistory: chatHistoryProp = [], programDetails: programDetailsProp = null, onTopUpCredits, isChatOpen = false }) => {
    useGeo(); // re-render with the API's lists once they load
    const history = useHistory();
    const { isDark } = useColorMode();
    const { authUser } = useAuth() || {};
    const dqcRange = getSolutionDqcRange(authUser);
    const [local, setLocal] = useState({ ...wizard });
    const [initialValues, setInitialValues] = useState(null); //state to check if wizard values have changed
    const [frameworks, setFrameworks] = useState([NONE_FRAMEWORK]);
    const [frameworksLoading, setFrameworksLoading] = useState(false);
    const [frameworkCatalog, setFrameworkCatalog] = useState([]);
    const [compatibleFrameworkIds, setCompatibleFrameworkIds] = useState(null);
    const initialFetchDoneRef = useRef(false);
    const [availableCompetencies, setAvailableCompetencies] = useState([]);
    const [competenciesLoading, setCompetenciesLoading] = useState(false);
    // Saved chat state the competency options fall back on when no suggested list exists
    const [savedChat, setSavedChat] = useState({ chatHistory: [], detectedCompetencies: [] });
    const [isSaving, setIsSaving] = useState(false);
    const [buildError, setBuildError] = useState(null);
    // A rebuild refused for credits: the shortfall and who can act on it (data.payer).
    const [buildShortfall, setBuildShortfall] = useState(null);
    // What saving would cost now: the program's first edit is free (null while unknown).
    const [editCost, setEditCost] = useState(null);
    const [editCostKey, setEditCostKey] = useState(0);

    useEffect(() => {
        if (!open || !programCode) return undefined;
        let cancelled = false;
        setEditCost(null);
        getLearningProgramEditCostService(programCode, local.NumSessions)
            .then((res) => { if (!cancelled) setEditCost(res?.data || null); })
            .catch(() => { if (!cancelled) setEditCost(null); });
        return () => { cancelled = true; };
    }, [open, programCode, local.NumSessions, editCostKey]);

    useEffect(() => {
        if (!open) return;

        let isCancelled = false;
        const best = hasMeaningfulWizardData(wizard)
            ? { ...wizard }
            : (hasMeaningfulWizardData(programDetailsProp) ? { ...programDetailsProp } : { ...wizard });

        // Only reset local/initialValues when local doesn't already have meaningful data.
        // If the user has already started editing (local has real data), preserve their work —
        // don't overwrite it just because a parent prop changed asynchronously.
        if (!hasMeaningfulWizardData(local) || initialValues === null) {
            setLocal(best);
            setInitialValues(best);
        }

        const hydrateWizard = async () => {
            if (!programCode) return;
            // Always hydrate on the very first open (initialValues === null) so that
            // any missing fields get filled from the server even when `best` already
            // has *partial* wizard data (e.g. just Theme/Title from chat markers but
            // missing Age, Countries, NumSessions, SessionDuration, etc.).
            // On subsequent re-opens skip the round-trip if local is already populated.
            if (initialValues !== null && hasMeaningfulWizardData(local)) return;

            try {
                const [savedResult, insiderResult] = await Promise.allSettled([
                    loadChatStateInsiderService(programCode),
                    getInsiderProgramService(programCode),
                ]);

                const savedRes = savedResult.status === 'fulfilled' ? savedResult.value : null;
                const insiderRes = insiderResult.status === 'fulfilled' ? insiderResult.value : null;

                const saved = savedRes?.data?.state_data || savedRes?.data || {};
                const wizardFromChat = saved?.wizard;
                const programDataFromApi = insiderRes?.data?.learning_program;
                const wizardFromProgram = programDataFromApi?.program_details;
                const remoteWizard = wizardFromChat || wizardFromProgram || programDetailsProp;

                if (!isCancelled) {
                    setSavedChat({
                        chatHistory: saved.chatHistory || [],
                        detectedCompetencies: saved.detectedCompetencies || [],
                    });
                }

                if (!remoteWizard || isCancelled) return;

                // Merge: remote (full server record) fills missing fields;
                // `best` (from props) overrides with any field it has — this
                // preserves values from the chat stream that may differ from a
                // stale server snapshot.
                // If Competencies are empty in wizard, fall back to programData dq_codes.
                const rawCompetencies = best?.Competencies?.length
                    ? best.Competencies
                    : (remoteWizard?.Competencies?.length ? remoteWizard.Competencies : (programDataFromApi?.dq_codes || []));

                const hydrated = {
                    ...remoteWizard,   // complete base from server
                    ...best,           // props override (fresher for chat-captured fields)
                    Competencies: rawCompetencies,
                    CourseCode: remoteWizard?.CourseCode || best?.CourseCode || programCode,
                };

                setLocal(hydrated);
                setInitialValues(hydrated);
            } catch (_) {}
        };

        hydrateWizard();
        return () => { isCancelled = true; };
    }, [open, wizard, programCode, programDetailsProp]); // eslint-disable-line react-hooks/exhaustive-deps

    const hasChanges = initialValues !== null && JSON.stringify(local) !== JSON.stringify(initialValues);

    // Fetch DQ competencies catalogue
    useEffect(() => {
        if (!open) return;
        let isCancelled = false;
        setCompetenciesLoading(true);
        listV3Level7DQCodeService('en-US')
            .then(res => {
                if (isCancelled) return;
                const list = res?.data?.metaData || res?.data?.dqco || [];
                setAvailableCompetencies(list);
            })
            .catch(() => {
                if (!isCancelled) setAvailableCompetencies([]);
            })
            .finally(() => {
                if (!isCancelled) setCompetenciesLoading(false);
            });
        return () => { isCancelled = true; };
    }, [open]);

    // DQ AI's suggested list is already scoped by the server: when present, the pruning
    // below and the save filter skip the tier/solution check and apply only the session cap.
    const suggestedCodes = normalizeCompetencyCodes(local.SuggestedCompetencies);

    // Automatically prune any selected competencies that violate the active tier or session capacity cap
    useEffect(() => {
        if (!open || !local.Competencies || local.Competencies.length === 0) return;
        const currentTier = resolveTier(local);
        const cap = getCompetencyCap(local.NumSessions);

        let modified = false;
        let filtered = local.Competencies.filter(c => {
            if (suggestedCodes.length > 0) return true;
            const code = typeof c === 'string' ? c : (c?.code || c?.dqco_no || String(c));
            const found = availableCompetencies.find(item => item.dqco_no === code);
            if (found && !isCompetencyInSolutionScope(found, currentTier, dqcRange)) {
                modified = true;
                return false;
            }
            return true;
        });

        if (filtered.length > cap) {
            filtered = filtered.slice(0, cap);
            modified = true;
        }

        if (modified) {
            setLocal(prev => ({ ...prev, Competencies: filtered }));
        }
    }, [open, local.Age, local.Tier, local.NumSessions, suggestedCodes.length, availableCompetencies, dqcRange]);

    const dqCodes = (Array.isArray(local.Competencies) ? local.Competencies : [])
        .map(c => typeof c === 'string' ? c : (c?.code || c?.dqco_no || ''))
        .filter(Boolean);
    const competenciesKey = [...dqCodes].sort().join(',');

    const [frameworksLoaded, setFrameworksLoaded] = useState(false);

    // Reset initial one-time fetch flag when dialog closes
    useEffect(() => {
        if (!open) {
            initialFetchDoneRef.current = false;
        }
    }, [open]);

    // Phase 1: One-time initial comprehensive fetch WITHOUT DQ codes when dialog opens.
    // This retrieves all frameworks for the country and age so that names (FrameName)
    // can be resolved for pre-selected frameworks in SelectedFrameList by matching framework_id.
    useEffect(() => {
        if (!open) return;
        const countryCode = local.Countries || null;
        const age         = local.Age        || null;
        if (!countryCode || !age) return;
        if (initialFetchDoneRef.current) return;
        initialFetchDoneRef.current = true;

        let isCancelled = false;
        const isEu = eu_countries.some(c => c.code === countryCode);

        listV3Level7FrameService(countryCode, isEu, age, null)
            .then(res => {
                if (isCancelled) return;
                const raw = res?.data?.frameworks || [];
                const seen = new Set();
                const normalized = [];
                raw.forEach(f => {
                    const id = f?.framework_id;
                    const name = f?.FrameName || f?.label || id;
                    if (id && !seen.has(id)) {
                        seen.add(id);
                        normalized.push({
                            value: id,
                            framework_id: id,
                            FrameName: name,
                            label: name,
                            DQCO: Array.isArray(f?.['DQCO#']) ? f['DQCO#'] : [],
                            SourceLink: f?.SourceLink || f?.['SourceLink (same as ProgL2)'] || '',
                        });
                    }
                });
                setFrameworkCatalog(prev => {
                    const map = new Map(prev.map(item => [item.framework_id, item]));
                    normalized.forEach(item => {
                        if (item.framework_id) map.set(item.framework_id, item);
                    });
                    return Array.from(map.values());
                });
            })
            .catch(() => {});

        return () => { isCancelled = true; };
    }, [open, local.Countries, local.Age]);

    // Phase 2: Subsequent / compatibility framework fetch (ALWAYS WITH DQ codes).
    // Re-fetches whenever country, age, or competencies change.
    // Guard: if no competency is selected (dqCodes.length === 0), framework fetching is blocked.
    useEffect(() => {
        if (!open) return;
        let isCancelled = false;
        const countryCode = local.Countries || null;
        const age         = local.Age        || null;

        if (!countryCode || !age) {
            setFrameworks([NONE_FRAMEWORK]);
            setCompatibleFrameworkIds(null);
            setFrameworksLoading(false);
            setFrameworksLoaded(true);
            return;
        }

        // If no competency is selected, do not immediately update frameworks or run compatibility check
        if (dqCodes.length === 0) {
            return;
        }

        const isEu = eu_countries.some(c => c.code === countryCode);
        setFrameworksLoading(true);
        setFrameworksLoaded(false);

        listV3Level7FrameService(countryCode, isEu, age, dqCodes)
            .then(res => {
                if (isCancelled) return;
                const raw = res?.data?.frameworks || [];
                const compatibleIds = new Set();
                const seen = new Set();
                const normalized = [];
                raw.forEach(cf => {
                    const id = cf?.framework_id;
                    const name = cf?.FrameName || cf?.label || id;
                    if (id) {
                        compatibleIds.add(String(id).trim());
                    }
                    if (id && !seen.has(id)) {
                        seen.add(id);
                        normalized.push({
                            value: id,
                            framework_id: id,
                            FrameName: name,
                            label: name,
                            DQCO: Array.isArray(cf?.['DQCO#']) ? cf['DQCO#'] : [],
                            SourceLink: cf?.SourceLink || cf?.['SourceLink (same as ProgL2)'] || '',
                        });
                    }
                });
                setCompatibleFrameworkIds(compatibleIds);
                setFrameworks([NONE_FRAMEWORK, ...normalized]);

                setFrameworkCatalog(prev => {
                    const map = new Map(prev.map(item => [item.framework_id, item]));
                    normalized.forEach(item => {
                        if (item.framework_id) map.set(item.framework_id, item);
                    });
                    return Array.from(map.values());
                });
            })
            .catch(() => {
                if (!isCancelled) {
                    setFrameworks([NONE_FRAMEWORK]);
                    setCompatibleFrameworkIds(new Set());
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setFrameworksLoading(false);
                    setFrameworksLoaded(true);
                }
            });

        return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, local.Countries, local.Age, competenciesKey]);

    const set = (key, val) => setLocal(prev => ({ ...prev, [key]: val }));

    // ── togglers ──────────────────────────────────────────────────────────

    const toggleFormat = (idx) => {
        setLocal(prev => {
            const updated = [...(prev.Format || [0, 0, 0, 0, 0, 0, 0])];
            updated[idx] = updated[idx] === 1 ? 0 : 1;
            return { ...prev, Format: updated };
        });
    };

    // BestDelivery stored as value-array post-wizard; bit-array at init (see normalise above)
    const toggleDelivery = (methodValue) => {
        setLocal(prev => {
            const current = normaliseBestDelivery(prev.BestDelivery);
            const next = current.includes(methodValue)
                ? current.filter(v => v !== methodValue)
                : current.length < 3 ? [...current, methodValue] : current;
            // Clear BestDeliveryOther if "Others" is deselected
            const extra = methodValue === 8 && current.includes(8) ? { BestDeliveryOther: '' } : {};
            return { ...prev, BestDelivery: next, ...extra };
        });
    };

    const handleSetActivityMode = (mode) => {
        if (mode === 'recommended') {
            setLocal(prev => ({ ...prev, Activities: null }));
        } else {
            setLocal(prev => {
                const currentTier = (prev.Tier && TIERS[prev.Tier]) ? prev.Tier : (resolveTier(prev) || 'T4');
                const rec = getRecommendedActivitiesForTier(currentTier);
                return {
                    ...prev,
                    Activities: [...new Set(rec)],
                };
            });
        }
    };

    const toggleActivity = (activityVal) => {
        setLocal(prev => {
            const currentTier = (prev.Tier && TIERS[prev.Tier]) ? prev.Tier : (resolveTier(prev) || 'T4');
            const available = TIER_ACTIVITIES[currentTier]?.available || [];
            const tierCap = Math.min(3, available.length);
            const current = Array.isArray(prev.Activities) && prev.Activities.length > 0
                ? prev.Activities
                : [...new Set(getRecommendedActivitiesForTier(currentTier))];
            let next;
            if (current.includes(activityVal)) {
                if (current.length === 1) return prev; // Cannot uncheck the last selected activity
                next = current.filter(a => a !== activityVal);
            } else {
                if (current.length >= tierCap) return prev; // Cannot exceed tier cap
                next = [...current, activityVal];
            }
            return { ...prev, Activities: next };
        });
    };

    // CulturalSensitivity: bit-array [0,0,0,0,0,0]. "None" = idx 4; mutually exclusive.
    const toggleSensitivity = (idx) => {
        setLocal(prev => {
            const current = Array.isArray(prev.CulturalSensitivity)
                ? [...prev.CulturalSensitivity]
                : [0, 0, 0, 0, 0, 0];
            if (idx === 4) {
                // None: clear everything else
                const next = [0, 0, 0, 0, 0, 0];
                next[4] = current[4] === 1 ? 0 : 1;
                return { ...prev, CulturalSensitivity: next, CulturalSensitivityDetails: '', CulturalSensitivityOther: '' };
            }
            current[4] = 0; // deselect None
            current[idx] = current[idx] === 1 ? 0 : 1;
            // Clear Other text if "Other" (idx 5) was just deselected
            const extra = idx === 5 && current[5] === 0 ? { CulturalSensitivityOther: '' } : {};
            return { ...prev, CulturalSensitivity: current, ...extra };
        });
    };

    const toggleLanguage = (lang) => {
        if (lang === DEFAULT_CONTENT_LANGUAGE) return; // permanent default base language
        setLocal(prev => {
            const current = Array.isArray(prev.ContentLanguages) ? prev.ContentLanguages : [];
            const next = current.includes(lang) ? current.filter(l => l !== lang) : [...current, lang];
            return { ...prev, ContentLanguages: next };
        });
    };

    const handleSave = async () => {
        if (!local.Competencies || local.Competencies.length === 0) return;
        if ((local.CurriculumType === 1 || local.CurriculumType === '1') && (!local.SubjectArea || (local.SubjectArea === 10 && (!local.SubjectAreaOther || !local.SubjectAreaOther.trim())))) return;

        // Compute the wizard to pass to onSave regardless of API success.
        // Declared outside try so it's accessible in the finally/post-try code.
        let savedProgramDetails = { ...local };
        let buildSucceeded = false;

        setIsSaving(true);
        setBuildError(null);
        setBuildShortfall(null);
        try {
            // Step 1: fetch full program record and existing chat state in parallel.
            // This always fetches fresh data so we never rely on potentially stale props.
            const [insiderResult, loadResult] = await Promise.allSettled([
                getInsiderProgramService(programCode),
                loadChatStateInsiderService(programCode),
            ]);

            const insiderRes = insiderResult.status === 'fulfilled' ? insiderResult.value : null;
            const loadRes   = loadResult.status   === 'fulfilled' ? loadResult.value   : null;

            const programData    = insiderRes?.data?.learning_program;
            const backendDetails = programData?.program_details || null;

            // --- Determine the authoritative wizard base ---
            // Priority: local (user edits) > backendDetails > programDetailsProp
            // If local has meaningful data (Age/Theme/Title etc.), use it directly.
            // Otherwise, use backendDetails as the authoritative base and merge local on top —
            // this preserves all program fields while still applying any partial user changes.
            const authBase = backendDetails || programDetailsProp || {};
            let programDetails;
            if (hasMeaningfulWizardData(local)) {
                programDetails = { ...local };
            } else {
                // Merge: auth base provides the full program data; local contributes any edits
                // the user made even if the form wasn't fully pre-filled.
                programDetails = { ...authBase, ...local };
            }
            savedProgramDetails = programDetails; // expose for post-try use

            // Auto-compute SuggestApproxHours from NumSessions × SessionDuration
            if (programDetails.NumSessions && programDetails.SessionDuration) {
                programDetails.SuggestApproxHours = programDetails.NumSessions * programDetails.SessionDuration;
            }
            // Derive StructureType from NumSessions: 1 = single session, 2 = multi session
            if (programDetails.NumSessions) {
                programDetails.StructureType = programDetails.NumSessions === 1 ? 1 : 2;
            }
            // Normalise BestDelivery before saving so backend always gets value-array format
            programDetails.BestDelivery = normaliseBestDelivery(programDetails.BestDelivery);
            // Normalise Activities (null if recommended/empty, array of strings if custom, capped at 3)
            if (Array.isArray(local.Activities) && local.Activities.length > 0) {
                programDetails.Activities = local.Activities.slice(0, 3);
            } else {
                programDetails.Activities = null;
            }
            // Ensure ContentLanguages contains only secondary translation languages (exclude default language)
            if (Array.isArray(programDetails.ContentLanguages)) {
                programDetails.ContentLanguages = programDetails.ContentLanguages.filter(l => l !== DEFAULT_CONTENT_LANGUAGE);
            }
            // Flatten SelectedFrameList to an array of framework_id strings only (excluding 'none')
            programDetails.SelectedFrameList = (programDetails.SelectedFrameList || [])
                .map(f => typeof f === 'string' ? f : (f?.framework_id || f?.value || String(f)))
                .filter(code => code && code !== 'none' && code !== NONE_FRAMEWORK.value);

            // Keep DetectedFrameworks ({framework_id, FrameName} objects read by UI
            // surfaces like the Framework Alignment card and chat-state restore)
            // in sync with the edited selection — otherwise it retains the stale
            // curation-time list after the user changes frameworks here.
            {
                const prevDetected = Array.isArray(programDetails.DetectedFrameworks)
                    ? programDetails.DetectedFrameworks : [];
                const allPool = [...frameworkCatalog, ...frameworks];
                programDetails.DetectedFrameworks = programDetails.SelectedFrameList
                    .map(code => {
                        const fromCatalog = allPool.find(f => f.framework_id === code || f.value === code);
                        const fromPrev = prevDetected.find(f => f?.framework_id === code);
                        return {
                            framework_id: code,
                            FrameName: fromCatalog?.FrameName || fromCatalog?.label || fromPrev?.FrameName || code,
                        };
                    });
            }

            // Synchronize audience tier and age
            const activeTier = resolveTier(local);
            if (activeTier) {
                programDetails.Tier = activeTier;
                programDetails.Age = TIER_TO_AGE_MAP[activeTier] || local.Age;
            }

            // Normalise Competencies as string array of codes, respecting tier eligibility and session capacity cap
            const saveCap = getCompetencyCap(programDetails.NumSessions || local.NumSessions);
            const validCompetencyCodes = (suggestedCodes.length > 0
                ? normalizeCompetencyCodes(local.Competencies)
                : (local.Competencies || [])
                    .map(c => typeof c === 'string' ? c : (c?.code || c?.dqco_no || String(c)))
                    .filter(code => {
                        const found = availableCompetencies.find(item => item.dqco_no === code);
                        return found && isCompetencyInSolutionScope(found, activeTier, dqcRange);
                    }))
                .slice(0, saveCap);
            programDetails.Competencies = validCompetencyCodes;

            const finalDqCodes = (Array.isArray(programDetails.Competencies) && programDetails.Competencies.length > 0)
                ? programDetails.Competencies
                : (programData?.dq_codes || []);

            // --- Determine existing state_data to preserve ---
            const loadedStateData = loadRes?.data?.state_data;
            const existingStateData = (loadedStateData && Object.keys(loadedStateData).length > 0)
                ? loadedStateData
                : { chatHistory: chatHistoryProp, step: 17 };
            await saveChatStateInsiderService(programCode, { ...existingStateData, wizard: programDetails });

            // Step 3: rebuild the program with updated wizard as program_details
            const buildResult = await buildLearningProgramService(
                programData?.code,
                programData?.full_name,
                programDetails.Age || programData?.target_audience,
                finalDqCodes,
                programData?.time_to_complete_minutes,
                programData?.image_url,
                programData?.theme_color,
                programData?.badge_background,
                programData?.primary_color,
                programData?.description,
                programData?.more_info,
                programData?.translations,
                programDetails,
                'retry'
            );

            const mlResp = buildResult?.data?.ml_server_resp;
            if (mlResp?.error) {
                const detail = mlResp?.body?.detail;
                let mlErrMsg;
                if (Array.isArray(detail) && detail.length > 0) {
                    const fields = detail
                        .map(d => d?.loc?.slice(-1)?.[0])
                        .filter(Boolean);
                    mlErrMsg = fields.length > 0
                        ? `Program generation failed: invalid or missing field(s) — ${fields.join(', ')}`
                        : `Program generation failed (code ${mlResp.http_code}).`;
                } else {
                    mlErrMsg =
                        mlResp?.body?.message ||
                        buildResult?.data?.message ||
                        `Program generation failed (code ${mlResp.http_code}).`;
                }
                throw new Error(mlErrMsg);
            }

            buildSucceeded = true;
        } catch (err) {
            console.warn('[EditProgramInfoDialog] handleSave failed:', err);
            const errMsg =
                err?.response?.data?.message ||
                err?.data?.message ||
                err?.message ||
                'Failed to save changes. Please try again.';
            setBuildError(errMsg);
            const errData = err?.body?.data;
            setBuildShortfall(errData?.reason === 'insufficient_credits' ? errData : null);
            return; // Do not close dialog or proceed
        } finally {
            setIsSaving(false);
        }

        // Close dialog and notify parent after a successful rebuild
        if (buildSucceeded) {
            onSave(savedProgramDetails);  // update local wizard state regardless of API result
            onClose();
            if (onSaved) onSaved();
        }
    };

    // ── Derived display values ────────────────────────────────────────────

    const activeTier          = (local.Tier && TIERS[local.Tier]) ? local.Tier : (resolveTier(local) || 'T4');
    const contentLanguages   = Array.isArray(local.ContentLanguages)   ? local.ContentLanguages   : [];
    const format             = Array.isArray(local.Format)             ? local.Format             : [0, 0, 0, 0, 0, 0, 0];
    const bestDelivery       = normaliseBestDelivery(local.BestDelivery);
    const culturalSensitivity = Array.isArray(local.CulturalSensitivity) ? local.CulturalSensitivity : [0, 0, 0, 0, 0, 0];
    const needsSensitivityDetails = culturalSensitivity.some((v, i) => v === 1 && i !== 4);
    const othersSelected     = culturalSensitivity[5] === 1; // "Other" sensitivity
    const othersDelivery     = bestDelivery.includes(8);     // "Others" teaching method
    const activeTierLabel        = getTierLabel(activeTier);
    const availableActivities    = getAvailableActivitiesForTier(activeTier);
    const recommendedLabels      = getRecommendedActivityLabels(activeTier);
    const activityCap            = Math.min(3, availableActivities.length);
    const isCustomActivities     = Array.isArray(local.Activities) && local.Activities.length > 0;
    const selectedActivities     = isCustomActivities ? local.Activities : [];
    const isIntegrated              = local.CurriculumType === 1 || local.CurriculumType === '1';
    const isSubjectAreaMissing      = isIntegrated && !local.SubjectArea;
    const isSubjectAreaOtherMissing = isIntegrated && local.SubjectArea === 10 && (!local.SubjectAreaOther || !local.SubjectAreaOther.trim());

    // Normalise SelectedFrameList → array of { value, label, framework_id, DQCO, isAvailable } objects
    // that can be used directly as the Autocomplete's value.
    // Only framework_id is used for identity, matching, and selection.
    const selectedFrameworks = (local.SelectedFrameList || []).map(f => {
        const fwId = typeof f === 'string' ? f : (f?.framework_id || f?.value || String(f));
        if (fwId === 'none' || f?.value === 'none' || f?.framework_id === 'none') {
            return { ...NONE_FRAMEWORK, isAvailable: true };
        }

        const combinedPool = [...frameworkCatalog, ...frameworks];
        const found = combinedPool.find(fw => fw && fw.framework_id === fwId);

        const targetFrameworkId = found?.framework_id || (typeof f === 'object' ? f.framework_id : null) || fwId;
        const resolvedName = found?.FrameName || found?.label
            || (typeof f === 'object' ? (f.FrameName || f.label) : null)
            || fwId;

        // Determine compatibility:
        // Compare ONLY "framework_id" to do matching.
        // If no competencies are selected (dqCodes.length === 0), do not run compatibility check.
        let isAvailable = true;
        if (dqCodes.length > 0 && compatibleFrameworkIds !== null && !frameworksLoading) {
            isAvailable = Boolean(targetFrameworkId && compatibleFrameworkIds.has(String(targetFrameworkId).trim()));
        }

        return {
            value: targetFrameworkId,
            label: resolvedName,
            FrameName: resolvedName,
            framework_id: targetFrameworkId,
            DQCO: found?.DQCO || (Array.isArray(f?.DQCO) ? f.DQCO : []),
            isAvailable,
        };
    });

    const unavailableFrameworks = selectedFrameworks.filter(fw => !fw.isAvailable);
    const hasUnavailableFrameworks = unavailableFrameworks.length > 0;

    const handleRemoveUnavailableFrameworks = () => {
        const remaining = selectedFrameworks.filter(fw => fw.isAvailable);
        set('SelectedFrameList', remaining);
    };

    const competencyCap = getCompetencyCap(local.NumSessions);
    const { source: competencySource, options: competencyOptions } = resolveCompetencyOptions({
        suggested: suggestedCodes,
        chatHistory: chatHistoryProp && chatHistoryProp.length ? chatHistoryProp : savedChat.chatHistory,
        detected: savedChat.detectedCompetencies,
        catalog: availableCompetencies,
        selected: local.Competencies,
        tier: activeTier,
        dqcRange,
    });

    // Normalise Competencies → array of objects with code, dqco_no, dqc_no, full_name, label
    const selectedCompetencies = (Array.isArray(local.Competencies) ? local.Competencies : [])
        .map(c => {
            const code = typeof c === 'string' ? c : (c?.code || c?.dqco_no || String(c));
            const found = availableCompetencies.find(item => item.dqco_no === code);
            return {
                code,
                dqco_no: code,
                dqc_no: found?.dqc_no,
                full_name: found?.full_name || code,
                label: found ? `${found.dqco_no} - ${found.full_name}` : code,
            };
        });

    // ── Render ────────────────────────────────────────────────────────────

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            // This dialog can open alongside the floating DQ AI chat panel (fixed
            // right:24px, width:400px). Only reserve that strip when the chat is
            // actually mounted — otherwise the modal centers in the space to its
            // LEFT even with the chat closed, making it look off-center. On mobile
            // the chat is a full-screen overlay, so keep it centered regardless.
            sx={{ '& .MuiDialog-container': { width: { xs: '100%', md: isChatOpen ? 'calc(100% - 448px)' : '100%' } } }}
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', maxHeight: '90vh', backgroundColor: 'var(--brand-surface, #fff)', backgroundImage: 'none' } }}>

            {/* Header */}
            <Box sx={{
                background: HEADER_BG, px: 3, py: 2, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>
                    {/* Slides 10-13 — the dialog rebuilds, it does not merely edit. */}
                    Edit program
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#fff', p: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Credit notice */}
            <Box sx={{ px: 3, py: 1.2, background: isDark ? 'rgba(224,112,0,0.08)' : '#FFF8F3', borderBottom: isDark ? '1px solid rgba(224,112,0,0.2)' : '1px solid #FAE0CC', display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoOutlinedIcon style={{ fontSize: 16, color: '#E07000', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: '#E07000', fontStyle: 'italic' }}>
                    {/*
                      * Slides 10-13. Says what a rebuild DOES and what it leaves
                      * alone — a person about to spend credits needs the second
                      * half as much as the first — and, from the API, what this
                      * save costs: a program's first edit is free (16 Oct 2026).
                      */}
                    Saving rebuilds the program. Lessons, activities and both assessments are regenerated from these answers.{' '}
                    <EditCostLine
                        cost={editCost}
                        onTopUpCredits={onTopUpCredits
                            ? (info) => onTopUpCredits(info, () => setEditCostKey((k) => k + 1))
                            : null}
                    />
                    {' '}Your published programs are not affected.
                </Typography>
            </Box>

            {/* Scrollable body */}
            <DialogContent sx={{ px: 3, py: 2.5, overflowY: 'auto', backgroundColor: 'var(--brand-surface, #fff)' }}>

                {/* ── 1. Audience & Context ────────────────────────────── */}
                <SectionHeader title="Audience & Context" isDark={isDark} />

                <FieldLabel>Target Country / Region</FieldLabel>
                <TextField select fullWidth size="small" sx={{ mb: 2.5 }}
                    value={local.Countries || ''}
                    onChange={e => set('Countries', e.target.value)}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 260 } } } }}
                >
                    {getCountries().map(c => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
                </TextField>

                <FieldLabel>Target Age Group</FieldLabel>
                <TextField select fullWidth size="small" sx={{ mb: 2.5 }}
                    value={activeTier}
                    onChange={e => {
                        const selectedTier = e.target.value;
                        const mappedAge = TIER_TO_AGE_MAP[selectedTier] || '';
                        setLocal(prev => {
                            let nextPreferred = prev.Activities;
                            if (Array.isArray(nextPreferred) && nextPreferred.length > 0) {
                                const newAvailable = (TIER_ACTIVITIES[selectedTier]?.available || []);
                                const filtered = nextPreferred.filter(a => newAvailable.includes(a)).slice(0, 3);
                                nextPreferred = filtered.length > 0 ? filtered : null;
                            }
                            return {
                                ...prev,
                                Tier: selectedTier,
                                Age: mappedAge,
                                Activities: nextPreferred,
                            };
                        });
                    }}
                >
                    {Object.keys(TIERS).map(tierKey => (
                        <MenuItem key={tierKey} value={tierKey}>
                            {getTierLabel(tierKey)}
                        </MenuItem>
                    ))}
                </TextField>

                <FieldLabel>Curriculum Type</FieldLabel>
                <RadioGroup row
                    value={String(local.CurriculumType || '')}
                    onChange={e => {
                        const val = parseInt(e.target.value);
                        setLocal(prev => ({
                            ...prev,
                            CurriculumType: val,
                            ...(val === 2 ? { SubjectArea: null, SubjectAreaOther: null, SubjectContextTopic: '' } : {}),
                        }));
                    }}
                    sx={{ mb: isIntegrated ? 2 : 0 }}
                >
                    <FormControlLabel value="2"
                        control={<Radio sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE } }} />}
                        label={<Typography sx={{ fontSize: 14 }}>Standalone AI/Digital Literacy curriculum</Typography>}
                    />
                    <FormControlLabel value="1"
                        control={<Radio sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE } }} />}
                        label={<Typography sx={{ fontSize: 14 }}>Integrated into existing subjects</Typography>}
                    />
                </RadioGroup>

                {/* Integrated-only: Subject Area + context topic */}
                {isIntegrated && (
                    <Box sx={{ mt: 2 }}>
                        <FieldLabel>Subject Area</FieldLabel>
                        <TextField select fullWidth size="small" sx={{ mb: isSubjectAreaMissing ? 1 : 2.5 }}
                            error={isSubjectAreaMissing}
                            value={local.SubjectArea || ''}
                            onChange={e => {
                                const val = parseInt(e.target.value, 10);
                                setLocal(prev => ({
                                    ...prev,
                                    SubjectArea: val,
                                    SubjectAreaOther: val !== 10 ? null : prev.SubjectAreaOther,
                                }));
                            }}
                            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 260 } } } }}
                        >
                            <MenuItem value="" disabled>Select subject</MenuItem>
                            {SUBJECTS.map(sub => <MenuItem key={sub.value} value={sub.value}>{sub.label}</MenuItem>)}
                        </TextField>
                        {isSubjectAreaMissing && (
                            <Typography sx={{ fontSize: 12, color: '#d32f2f', mb: 2 }}>
                                Please select a subject area to rebuild the program.
                            </Typography>
                        )}

                        {local.SubjectArea === 10 && (
                            <>
                                <FieldLabel>Please specify other subject</FieldLabel>
                                <TextField fullWidth size="small" sx={{ mb: isSubjectAreaOtherMissing ? 1 : 2.5 }}
                                    error={isSubjectAreaOtherMissing}
                                    placeholder="Please specify other subject"
                                    value={local.SubjectAreaOther || ''}
                                    onChange={e => set('SubjectAreaOther', e.target.value)}
                                />
                                {isSubjectAreaOtherMissing && (
                                    <Typography sx={{ fontSize: 12, color: '#d32f2f', mb: 2 }}>
                                        Please specify the subject to rebuild the program.
                                    </Typography>
                                )}
                            </>
                        )}

                        <FieldLabel>Learning Unit / Topic Context</FieldLabel>
                        <TextField fullWidth multiline minRows={2} size="small" sx={{ mb: 2.5 }}
                            placeholder="Describe the specific learning unit or topic in this subject (e.g., 'data interpretation in Mathematics')"
                            value={local.SubjectContextTopic || ''}
                            onChange={e => set('SubjectContextTopic', e.target.value)}
                        />
                    </Box>
                )}

                <Divider sx={{ my: 2.5 }} />

                {/* ── 2. Learning Theme & Language ─────────────────────── */}
                <SectionHeader title="Learning Theme & Language" isDark={isDark} />

                <FieldLabel>Topic / Theme Description</FieldLabel>
                <TextField fullWidth multiline minRows={3} sx={{ mb: 2.5 }}
                    placeholder="Describe the learning topic or theme…"
                    value={local.Theme || ''}
                    onChange={e => set('Theme', e.target.value)}
                />

                {/* Competencies — searchable Autocomplete (capped by min(3, sessions)) */}
                <FieldLabel>
                    Competencies
                </FieldLabel>
                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #8A8F98)', mb: 1 }}>
                    {competencySource !== 'scope'
                        ? 'Choose from the competencies DQ AI suggested for this program.'
                        : 'You can select up to 1 competency per session (maximum of 3).'}
                </Typography>

                <Box sx={{
                    border: `1.5px solid ${selectedCompetencies.length === 0 ? '#d32f2f' : ORANGE}`,
                    borderRadius: '8px',
                    px: 1.5,
                    pt: selectedCompetencies.length > 0 ? 1.5 : 0,
                    pb: 0,
                    mb: selectedCompetencies.length === 0 ? 1 : 2.5,
                    maxHeight: 190,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.18)' : '#D8D2CC', borderRadius: 3 },
                    transition: 'border-color 0.15s',
                }}>
                    {/* Removable chips for selected competencies */}
                    {selectedCompetencies.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                            {selectedCompetencies.map(comp => {
                                const dotColor = DQC_COLORS[comp.dqc_no] || ORANGE;
                                return (
                                    <Chip
                                        key={comp.code}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Box sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: dotColor,
                                                    flexShrink: 0,
                                                }} />
                                                <span>{comp.label}</span>
                                            </Box>
                                        }
                                        size="small"
                                        onDelete={() => {
                                            const next = selectedCompetencies.filter(c => c.code !== comp.code).map(c => c.code);
                                            set('Competencies', next);
                                        }}
                                        sx={{
                                            background: isDark ? 'rgba(238,102,40,0.18)' : '#FFF0E8',
                                            color: ORANGE,
                                            fontWeight: 600,
                                            border: `1px solid ${ORANGE}`,
                                            '& .MuiChip-deleteIcon': { color: ORANGE, '&:hover': { color: '#c44' } },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}

                    {/* Autocomplete search */}
                    <Autocomplete
                        multiple
                        options={competencyOptions}
                        getOptionLabel={opt => opt.label || (opt.dqco_no && opt.full_name ? `${opt.dqco_no} - ${opt.full_name}` : (opt.dqco_no || opt.code || ''))}
                        isOptionEqualToValue={(opt, val) => (opt.dqco_no || opt.code) === (val.dqco_no || val.code)}
                        value={selectedCompetencies}
                        onChange={(_, newValue) => {
                            let updated = newValue;
                            if (updated.length > competencyCap) updated = updated.slice(0, competencyCap);
                            const codes = updated.map(c => c.code || c.dqco_no);
                            set('Competencies', codes);
                        }}
                        loading={competenciesLoading}
                        renderTags={() => null}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            const dotColor = DQC_COLORS[option.dqc_no] || ORANGE;
                            return (
                                <li key={key || option.dqco_no} {...optionProps}>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: dotColor,
                                            mr: 1.25,
                                            flexShrink: 0,
                                        }}
                                    />
                                    {option.full_name ? `${option.dqco_no} - ${option.full_name}` : option.dqco_no}
                                </li>
                            );
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder={
                                    competenciesLoading
                                        ? 'Loading competencies…'
                                        : selectedCompetencies.length >= competencyCap
                                            ? `Maximum of ${competencyCap} ${competencyCap === 1 ? 'competency' : 'competencies'} reached`
                                            : 'Search and select competencies…'
                                }
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {competenciesLoading
                                                ? <CircularProgress color="inherit" size={14} sx={{ mr: 1 }} />
                                                : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                    '& .MuiInputBase-root': { px: 0 },
                                }}
                            />
                        )}
                        disabled={selectedCompetencies.length >= competencyCap}
                        sx={{ '& .MuiAutocomplete-inputRoot': { p: 0 } }}
                    />
                </Box>
                {selectedCompetencies.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: '#d32f2f', mb: 2 }}>
                        At least one competency is required to rebuild the program.
                    </Typography>
                )}

                {/* SelectedFrameList — searchable Autocomplete (same pattern as old wizard) */}
                <FieldLabel>
                    Framework Alignment
                </FieldLabel>
                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #8A8F98)', mb: 1 }}>
                    You can select up to 5 frameworks.
                </Typography>

                {/* Bordered container: chips on top, search input below */}
                {/*
                  * Slide 10: "Frameworks all in this box that is scrollable to save
                  * space." A program can carry a dozen, and unbounded chips pushed
                  * every field below them off the dialog. Capped and scrolled, so
                  * the box holds its height however many are chosen.
                  */}
                <Box sx={{
                    border: `1.5px solid ${hasUnavailableFrameworks ? '#d32f2f' : selectedFrameworks.length > 0 ? ORANGE : (isDark ? 'rgba(255,255,255,0.12)' : '#E0E0E0')}`,
                    borderRadius: '8px',
                    px: 1.5,
                    pt: selectedFrameworks.length > 0 ? 1.5 : 0,
                    pb: 0,
                    mb: hasUnavailableFrameworks ? 1 : 2.5,
                    maxHeight: 190,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.18)' : '#D8D2CC', borderRadius: 3 },
                    transition: 'border-color 0.15s',
                }}>
                    {/* Removable chips for selected frameworks */}
                    {selectedFrameworks.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                            {selectedFrameworks.map(fw => {
                                const isUnavailable = !fw.isAvailable;
                                return (
                                    <Chip
                                        key={fw.framework_id || fw.value}
                                        label={
                                            <span style={{
                                                textDecoration: isUnavailable ? 'line-through' : 'none',
                                                opacity: isUnavailable ? 0.85 : 1,
                                            }}>
                                                {fw.framework_id === 'none' ? 'No Specific Framework' : (fw.label || fw.FrameName || fw.framework_id)}
                                            </span>
                                        }
                                        size="small"
                                        onDelete={() => {
                                            const next = selectedFrameworks.filter(f => (f.framework_id || f.value) !== (fw.framework_id || fw.value));
                                            set('SelectedFrameList', next);
                                        }}
                                        sx={{
                                            background: isUnavailable
                                                ? (isDark ? 'rgba(211,47,47,0.2)' : '#FFEBEE')
                                                : (isDark ? 'rgba(238,102,40,0.18)' : '#FFF0E8'),
                                            color: isUnavailable ? '#d32f2f' : ORANGE,
                                            fontWeight: 600,
                                            border: `1px solid ${isUnavailable ? '#d32f2f' : ORANGE}`,
                                            '& .MuiChip-deleteIcon': {
                                                color: isUnavailable ? '#d32f2f' : ORANGE,
                                                '&:hover': { color: '#c44' },
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}

                    {/* Autocomplete search — chips rendered above, not inline */}
                    <Autocomplete
                        multiple
                        options={frameworks}
                        getOptionLabel={opt => (opt?.framework_id === 'none' ? 'No Specific Framework' : (opt?.FrameName || opt?.label || opt?.framework_id || ''))}
                        isOptionEqualToValue={(opt, val) => {
                            if (!opt || !val) return false;
                            const optId = opt.framework_id || opt.value;
                            const valId = val.framework_id || val.value;
                            return optId === valId;
                        }}
                        value={selectedFrameworks}
                        onChange={(_, newValue) => {
                            const lastAdded = newValue[newValue.length - 1];
                            let updated = newValue;
                            if (lastAdded?.value === 'none' || lastAdded?.framework_id === 'none') {
                                updated = [lastAdded];          // "No Specific Framework" clears all
                            } else {
                                updated = newValue.filter(f => f.value !== 'none' && f.framework_id !== 'none'); // real fw removes 'none'
                            }
                            if (updated.length > 5) updated = updated.slice(0, 5);
                            set('SelectedFrameList', updated);
                        }}
                        loading={frameworksLoading}
                        renderTags={() => null}   // chips rendered in the box above
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder={
                                    frameworksLoading
                                        ? 'Loading frameworks…'
                                        : selectedFrameworks.length >= 5
                                            ? 'Maximum of 5 frameworks reached'
                                            : 'Type a framework or standard here…'
                                }
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {frameworksLoading
                                                ? <CircularProgress color="inherit" size={14} sx={{ mr: 1 }} />
                                                : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                    '& .MuiInputBase-root': { px: 0 },
                                }}
                            />
                        )}
                        disabled={selectedFrameworks.length >= 5 && !selectedFrameworks.some(f => f.value === 'none')}
                        sx={{ '& .MuiAutocomplete-inputRoot': { p: 0 } }}
                    />
                </Box>
                {hasUnavailableFrameworks && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 12, color: '#d32f2f' }}>
                            Some selected frameworks are not available for the selected region/age group/competencies.
                        </Typography>
                        <Button
                            size="small"
                            onClick={handleRemoveUnavailableFrameworks}
                            sx={{
                                textTransform: 'none',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#d32f2f',
                                p: '2px 8px',
                                minWidth: 0,
                                border: '1px solid #d32f2f',
                                borderRadius: '6px',
                                background: isDark ? 'rgba(211,47,47,0.1)' : '#FFF0F0',
                                '&:hover': {
                                    background: isDark ? 'rgba(211,47,47,0.2)' : '#FFE0E0',
                                    borderColor: '#b71c1c',
                                    color: '#b71c1c',
                                },
                            }}
                        >
                            Remove unavailable
                        </Button>
                    </Box>
                )}

                {SHOW_CONTENT_LANGUAGES && (<>
                <FieldLabel>Content Language <Box component="span" sx={{ fontWeight: 400, color: 'var(--brand-text-muted, #888)', fontSize: 13 }}>(select all that apply)</Box></FieldLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {Object.entries(LANGUAGES_SUPPORTED).map(([code, label]) => {
                        const isDefaultLang = code === DEFAULT_CONTENT_LANGUAGE;
                        const isChecked = isDefaultLang || contentLanguages.includes(code);

                        return (
                            <FormControlLabel key={code}
                                control={
                                    <Checkbox
                                        checked={isChecked}
                                        disabled={isDefaultLang}
                                        onChange={() => !isDefaultLang && toggleLanguage(code)}
                                        sx={{
                                            color: ORANGE,
                                            '&.Mui-checked': { color: ORANGE },
                                            '&.Mui-disabled': { color: `${ORANGE} !important`, opacity: 0.65 },
                                        }}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: 14 }}>
                                        {label} {isDefaultLang && <Box component="span" sx={{ fontSize: 12, color: 'var(--brand-text-muted, #888)' }}>(Default)</Box>}
                                    </Typography>
                                }
                                sx={{ mr: 0 }}
                            />
                        );
                    })}
                </Box>
                </>)}

                <Divider sx={{ my: 2.5 }} />

                {/* ── 3. Program Structure ─────────────────────────────── */}
                <SectionHeader title="Program Structure" isDark={isDark} />

                <FieldLabel>
                    Number of Sessions
                </FieldLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => {
                        const selected = local.NumSessions === n;
                        return (
                            <Box key={n} onClick={() => set('NumSessions', n)} sx={{
                                width: 44, height: 44, borderRadius: '50%', fontSize: 14,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                                border: `2px solid ${selected ? ORANGE : (isDark ? 'rgba(255,255,255,0.15)' : '#D0D0D0')}`,
                                background: selected ? ORANGE : (isDark ? 'transparent' : '#fff'),
                                color: selected ? '#fff' : 'var(--brand-text, #333)',
                                fontWeight: selected ? 700 : 400,
                                '&:hover': { borderColor: ORANGE },
                            }}>{n}</Box>
                        );
                    })}
                    {/* Custom input commented out: maximum 10 sessions allowed for now
                    <TextField placeholder="11-20" size="small"
                        value={local.NumSessions > 10 ? local.NumSessions : ''}
                        onChange={e => {
                            const v = parseInt(e.target.value);
                            if (!isNaN(v) && v > 10 && v <= 20) set('NumSessions', v);
                            else if (e.target.value === '') set('NumSessions', null);
                        }}
                        inputProps={{ style: { textAlign: 'center' } }}
                        sx={{ width: 80, '& .MuiOutlinedInput-root': { height: 44, borderRadius: '8px' } }}
                    />
                    */}
                </Box>

                {/*
                  * Slide 11: "The user does not select the session duration either."
                  *
                  * The engine decides it — its own instructions read "the person does
                  * not choose how long a session is: every session is generated at 30
                  * to 45 minutes (45 by default). Never ask for a session length."
                  * The control asked a question the builder ignores, so an answer
                  * given here was quietly discarded. local.SessionDuration is still
                  * read and sent — it is simply no longer the person's to set.
                  */}
                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #8A8F98)', mb: 1 }}>
                    Sessions run 30 to 45 minutes. LearnCreator sets the length to suit the material.
                </Typography>

                <Divider sx={{ my: 2.5 }} />

                {/*
                   * REMOVED — Learning Focus Areas.
                   *
                   * Seongmin, 7 Sep, against these mock-ups: "I have marked the
                   * parameters that the latest version of LearnCreator does not use in
                   * program creation." This was one of them. It asked the person to
                   * decide something the builder never reads, inside a dialog that
                   * spends credits — which implied the answer changed the result.
                   */}
                  
                                  {/* ── 5. Prior Learner Exposure ────────────────────────── */}
                <SectionHeader title="Prior Learner Exposure" isDark={isDark} />

                <FieldLabel>Learners' prior exposure to this topic</FieldLabel>
                <RadioGroup row
                    value={String(local.ExposureTopic || '')}
                    onChange={e => set('ExposureTopic', parseInt(e.target.value))}
                    sx={{ mb: 2, flexWrap: 'wrap' }}
                >
                    {EXPOSURE_OPTIONS.map(({ label, val, desc }) => (
                        <FormControlLabel key={val} value={String(val)}
                            control={<Radio sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE } }} />}
                            label={
                                <Box>
                                    <Typography sx={{ fontSize: 14 }}>{label}</Typography>
                                    {desc && <Typography sx={{ fontSize: 12, color: 'var(--brand-text-muted, #888)' }}>{desc}</Typography>}
                                </Box>
                            }
                            sx={{ mr: 2, mb: 0.5 }}
                        />
                    ))}
                </RadioGroup>

                {(local.ExposureTopic === 2 || local.ExposureTopic === 3) && (
                    <>
                        <FieldLabel optional>Describe prior exposure</FieldLabel>
                        <TextField fullWidth multiline minRows={2} sx={{ mb: 2.5 }}
                            placeholder="e.g., key concepts covered, formats used, depth of discussion"
                            value={local.ExposureTopicDetail || ''}
                            onChange={e => set('ExposureTopicDetail', e.target.value)}
                        />
                    </>
                )}

                <Divider sx={{ my: 2.5 }} />

                {/*
                   * REMOVED — Delivery & Environment and Tech Environment.
                   *
                   * Seongmin, 7 Sep, against these mock-ups: "I have marked the
                   * parameters that the latest version of LearnCreator does not use in
                   * program creation." This was one of them. It asked the person to
                   * decide something the builder never reads, inside a dialog that
                   * spends credits — which implied the answer changed the result.
                   */}
                  
                {/* ── 8. Activity Preferences ──────────────────────────── */}
                <SectionHeader title="Activity Preferences" isDark={isDark} />

                <FieldLabel>How would you like activities to be included?</FieldLabel>
                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #777)', mb: 1.5 }}>
                    Choose whether to use the balanced activities recommended for this age tier, or customize which activity types to include.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                    <SelectableRow
                        useRadio
                        checked={!isCustomActivities}
                        onClick={() => handleSetActivityMode('recommended')}
                        label={`Recommended for ${activeTierLabel}`}
                        sublabel={`Automatically balances activities across learning objectives. Includes: ${recommendedLabels.join(', ')}.`}
                        isDark={isDark}
                    />
                    <SelectableRow
                        useRadio
                        checked={isCustomActivities}
                        onClick={() => handleSetActivityMode('custom')}
                        label="Custom Selection"
                        sublabel="Select the specific activity types to include across this module's learning objectives."
                        isDark={isDark}
                    />
                </Box>

                {isCustomActivities && (
                    <Box sx={{ mt: 1, mb: 1.5, pl: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #777)', mb: 0.5 }}>
                            Available for {activeTierLabel} (
                            <Box component="span" sx={{ fontWeight: 600, color: ORANGE }}>
                                {selectedActivities.length} of {activityCap} selected
                            </Box>
                            ):
                        </Typography>
                        {availableActivities.map(act => {
                            const isChecked = selectedActivities.includes(act.value);
                            const cannotUncheck = isChecked && selectedActivities.length === 1;
                            const cannotCheck = !isChecked && selectedActivities.length >= activityCap;
                            return (
                                <SelectableRow
                                    key={act.value}
                                    checked={isChecked}
                                    disabled={cannotUncheck || cannotCheck}
                                    onClick={() => toggleActivity(act.value)}
                                    label={act.label}
                                    sublabel={act.description}
                                    isDark={isDark}
                                />
                            );
                        })}
                    </Box>
                )}

                <Divider sx={{ my: 2.5 }} />

                {/* ── 9. Cultural Sensitivities ────────────────────────── */}
                <SectionHeader title="Cultural Sensitivities" isDark={isDark} />

                <FieldLabel>Are there any sensitivities we should keep in mind?</FieldLabel>
                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #777)', mb: 1.5 }}>
                    "None" is mutually exclusive with other options.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                    {SENSITIVITIES.map((item, idx) => {
                        const selected   = culturalSensitivity[idx] === 1;
                        const noneActive = culturalSensitivity[4] === 1;
                        const isNone     = idx === 4;
                        const disabled   = (isNone && needsSensitivityDetails) || (!isNone && noneActive);
                        return (
                            <SelectableRow key={item.value}
                                checked={selected}
                                disabled={disabled}
                                onClick={() => toggleSensitivity(idx)}
                                label={item.label}
                                sublabel={item.sublabel}
                                isDark={isDark}
                            />
                        );
                    })}
                </Box>
                {/* CulturalSensitivityOther: shown when "Other" (idx 5) is selected */}
                {othersSelected && (
                    <>
                        <FieldLabel optional>Please specify other sensitivity</FieldLabel>
                        <TextField fullWidth size="small" sx={{ mt: 0.5, mb: 2 }}
                            placeholder="Describe other sensitivity or consideration…"
                            value={local.CulturalSensitivityOther || ''}
                            onChange={e => set('CulturalSensitivityOther', e.target.value)}
                        />
                    </>
                )}
                {/* CulturalSensitivityDetails: shown when any non-None sensitivity is selected */}
                {needsSensitivityDetails && (
                    <>
                        <FieldLabel>Please share more context</FieldLabel>
                        <Typography sx={{ fontSize: 12, color: 'var(--brand-text-muted, #888)', mb: 1 }}>
                            e.g., "Avoid political topics", "Use visuals appropriate for Islamic schools", "Respect norms about boys and girls working separately"
                        </Typography>
                        <TextField fullWidth multiline minRows={3} sx={{ mb: 1 }}
                            placeholder="Describe any specific sensitivities or guidelines…"
                            value={local.CulturalSensitivityDetails || ''}
                            onChange={e => set('CulturalSensitivityDetails', e.target.value)}
                        />
                    </>
                )}

                <Divider sx={{ my: 2.5 }} />

                {/* ── 10. Content Style ────────────────────────────────── */}
                <SectionHeader title="Content Style" isDark={isDark} />

                <FieldLabel>Choose the visual style for your program content</FieldLabel>
                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #777)', mb: 1.5 }}>Select one style.</Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 2, mb: 1,
                }}>
                    {CONTENT_STYLES.map(style => {
                        const selected = local.ContentStyle === style.value;
                        return (
                            <Box key={style.value} onClick={() => set('ContentStyle', style.value)} sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
                                border: `2px solid ${selected ? ORANGE : (isDark ? 'rgba(255,255,255,0.15)' : '#E0E0E0')}`,
                                transition: 'all 0.15s',
                                '&:hover': { borderColor: ORANGE },
                            }}>
                                <Box component="img" src={style.image} alt={style.label}
                                    sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }}
                                />
                                <Typography sx={{
                                    fontSize: 12, fontWeight: selected ? 700 : 400, py: 0.75, px: 1,
                                    color: selected ? ORANGE : 'var(--brand-text, #333)', textAlign: 'center',
                                    background: selected ? (isDark ? 'rgba(255,105,0,0.15)' : '#FFF5F0') : 'var(--brand-surface, #fff)', width: '100%',
                                }}>
                                    {style.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

            </DialogContent>

            {/* Footer */}
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--brand-border, #EBEBEB)', gap: 1, flexWrap: 'wrap', flexDirection: 'column', alignItems: 'stretch' }}>
                {buildError && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, background: isDark ? 'rgba(211,47,47,0.12)' : '#FFF0F0', border: `1px solid ${isDark ? 'rgba(211,47,47,0.35)' : '#FFCDD2'}`, borderRadius: '8px', width: '100%' }}>
                        <InfoOutlinedIcon style={{ fontSize: 16, color: '#D32F2F', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, color: '#D32F2F' }}>
                            {buildError}
                            {/* On a credit shortfall, the workspace owner can top up right here;
                                anyone else has already been told whom to ask. */}
                            {buildShortfall && canBuy(buildShortfall.payer) && (
                                <Typography
                                    component="span"
                                    onClick={() => (onTopUpCredits
                                        ? onTopUpCredits(buildShortfall)
                                        : history.push('/account/settings?topup=1'))}
                                    sx={{ fontSize: 13, fontWeight: 700, color: '#D32F2F', textDecoration: 'underline', cursor: 'pointer', ml: 0.5 }}
                                >
                                    {topUpLabel(buildShortfall.payer)}
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                {!hasChanges && !isSaving && (
                    <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #999)', flex: 1, fontStyle: 'italic', alignSelf: 'center' }}>
                        No changes to save.
                    </Typography>
                )}
                {hasChanges && !isSaving && (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <Button
                            size="small"
                            onClick={() => {
                                if (initialValues) {
                                    setLocal({ ...initialValues });
                                }
                            }}
                            startIcon={<RotateLeftIcon style={{ fontSize: 20 }} />}
                            sx={{
                                textTransform: 'none',
                                fontSize: 13,
                                fontWeight: 600,
                                color: 'var(--brand-text-muted, #777)',
                                '&:hover': {
                                    color: ORANGE,
                                    background: isDark ? 'rgba(238,102,40,0.1)' : 'rgba(238,102,40,0.06)',
                                },
                            }}
                        >
                            Reset changes
                        </Button>
                    </Box>
                )}
                <Button onClick={onClose} disabled={isSaving} variant="outlined" sx={{
                    textTransform: 'none', fontWeight: 600,
                    borderColor: 'var(--brand-border, #D0D0D0)', color: 'var(--brand-text-muted, #555)',
                    '&:hover': { borderColor: 'var(--brand-border)', background: isDark ? 'rgba(255,255,255,0.06)' : '#f5f5f5' },
                }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={
                        isSaving ||
                        frameworksLoading ||
                        hasUnavailableFrameworks ||
                        !hasChanges ||
                        !local.Competencies ||
                        local.Competencies.length === 0 ||
                        isSubjectAreaMissing ||
                        isSubjectAreaOtherMissing
                    }
                    variant="contained"
                    sx={{
                        textTransform: 'none', fontWeight: 700, minWidth: 140,
                        background: ORANGE, '&:hover': { background: '#d44d16' },
                        color: '#fff',
                        '&.Mui-disabled': { background: '#F0F0F0', color: '#aaa' },
                    }}
                >
                    {isSaving
                        ? <><CircularProgress size={15} sx={{ color: '#fff', mr: 1 }} />Saving…</>
                        : 'Save program'
                    }
                </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default EditProgramInfoDialog;
